import { ExternalLink, ListChecks, ScrollText } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { fullDate } from '@/lib/datetime';

import type { AlvoStatus, Incidente, OperacaoAlvo } from '../../../api/monitoramento';
import {
  useIncidentesDoAlvo,
  useOperacoesDoAlvo,
  useSerieAlvo,
} from '../../../hooks/useMonitoramentoQuery';
import { MonitoramentoStatusPill } from './MonitoramentoStatusPill';
import { UptimeBars } from './UptimeBars';

type Janela = '1h' | '6h' | '24h' | '7d';

/**
 * Sheet de detalhe do alvo (Estado D dos mockups). Mostra 3 stat cards,
 * uptime bars, tabela de operações afetadas, lista de incidentes do alvo
 * e bloco "Investigar" com deep-links para Jobs e Observabilidade.
 *
 * <p>Usa Sheet em vez de rota separada — preserva o contexto do dashboard
 * (não força reload e re-fetch da página principal). Pode ser fechado com
 * Esc ou clique fora; abre em desktop pela direita (60% da largura).
 */
export interface DetalheAlvoSheetProps {
  alvo: AlvoStatus | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Quando o usuário aciona um deep-link interno (ex.: "Ver jobs deste alvo")
   * que muda de tab dentro do dashboard, o Sheet é fechado para revelar a
   * mudança. Quando aciona link externo (logs em outra aba), permanece.
   */
  onDeepLinkInterno?: () => void;
}

export function DetalheAlvoSheet({
  alvo,
  open,
  onOpenChange,
  onDeepLinkInterno,
}: DetalheAlvoSheetProps) {
  const [janela, setJanela] = useState<Janela>('1h');
  const operacoesQuery = useOperacoesDoAlvo(open && alvo ? alvo.alvo : null, janela);
  const incidentesQuery = useIncidentesDoAlvo(open && alvo ? alvo.alvo : null, 10);
  const serieQuery = useSerieAlvo(open && alvo ? alvo.alvo : null, '7d', !!(open && alvo));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto p-6 sm:max-w-2xl lg:max-w-3xl">
        {alvo ? (
          <>
            <SheetHeader>
              <div className="flex items-center justify-between gap-3">
                <SheetTitle className="font-mono text-base">{alvo.alvo}</SheetTitle>
                <MonitoramentoStatusPill status={alvo.status} />
              </div>
              <SheetDescription>
                {alvo.incidente_ativo_id != null
                  ? `Incidente #${alvo.incidente_ativo_id} aberto (${alvo.incidente_ativo_severidade ?? '—'}).`
                  : 'Sem incidente aberto no momento.'}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-6">
              {/* Stat cards — sucesso e latência. Distintamente: % vem da
                  janela escolhida abaixo, p95 1h vem do snapshot agregado. */}
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                <StatCard
                  titulo="Uptime 24h"
                  valor={formatPercentual(alvo.uptime_24h)}
                />
                <StatCard
                  titulo="Uptime 7d"
                  valor={formatPercentual(alvo.uptime_7d)}
                />
                <StatCard
                  titulo="p95 latência (1h)"
                  valor={formatLatencia(alvo.latencia_p95_1h_ms)}
                />
              </div>

              {/* Uptime bars Kuma — 7 dias por hora (168 barras).
                  Na home essas bars não aparecem; só aqui no detalhe. */}
              <Card>
                <CardContent className="space-y-3 pt-4">
                  <div className="text-sm font-medium">Histórico (últimos 7 dias)</div>
                  {serieQuery.isLoading ? (
                    <Spinner size={32} label="Carregando série" />
                  ) : (
                    <UptimeBars buckets={serieQuery.data?.buckets ?? []} totalBarras={168} alvo={alvo?.alvo} />
                  )}
                </CardContent>
              </Card>

              {/* Operações afetadas */}
              <Card>
                <CardContent className="space-y-3 pt-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-medium">Operações nesta janela</div>
                    <select
                      value={janela}
                      onChange={(e) => setJanela(e.target.value as Janela)}
                      className="cursor-pointer rounded border bg-background px-2 py-1 text-xs"
                    >
                      <option value="1h">Última hora</option>
                      <option value="6h">Últimas 6h</option>
                      <option value="24h">Últimas 24h</option>
                      <option value="7d">Últimos 7 dias</option>
                    </select>
                  </div>
                  {operacoesQuery.isLoading ? (
                    <Spinner size={32} label="Carregando operações" />
                  ) : (operacoesQuery.data?.lista ?? []).length === 0 ? (
                    <EmptyState
                      icon={<ListChecks className="size-6" />}
                      title="Nenhuma chamada nesta janela"
                      description="Sem amostras registradas para o alvo no período selecionado."
                    />
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Operação</TableHead>
                          <TableHead className="hidden text-right sm:table-cell">Sucesso</TableHead>
                          <TableHead className="hidden text-right sm:table-cell">p95</TableHead>
                          <TableHead className="hidden md:table-cell">Último erro</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {operacoesQuery.data!.lista.map((op) => (
                          <LinhaOperacao key={op.operation} op={op} />
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Incidentes deste alvo */}
              <Card>
                <CardContent className="space-y-3 pt-4">
                  <div className="text-sm font-medium">Incidentes deste alvo</div>
                  {incidentesQuery.isLoading ? (
                    <Spinner size={32} label="Carregando incidentes" />
                  ) : (incidentesQuery.data?.lista ?? []).length === 0 ? (
                    <div className="text-xs italic text-muted-foreground">
                      Sem histórico de incidentes registrado para este alvo.
                    </div>
                  ) : (
                    <ListaIncidentes incidentes={incidentesQuery.data!.lista} />
                  )}
                </CardContent>
              </Card>

              {/* Investigar — deep-links contextuais */}
              <Card>
                <CardContent className="space-y-3 pt-4">
                  <div className="text-sm font-medium">Investigar</div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <Link
                        to={construirLinkJobsDoAlvo(alvo.alvo)}
                        onClick={onDeepLinkInterno}
                      >
                        <ListChecks className="mr-2 size-4" />
                        Ver jobs deste alvo
                      </Link>
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <Link
                        to={construirLinkLogsDoAlvo(alvo.alvo)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ScrollText className="mr-2 size-4" />
                        Ver logs deste alvo (24h)
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        ) : (
          <SheetHeader>
            <SheetTitle>Selecione um alvo</SheetTitle>
          </SheetHeader>
        )}
      </SheetContent>
    </Sheet>
  );
}

function StatCard({ titulo, valor }: { titulo: string; valor: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{titulo}</div>
        <div className="mt-1 text-xl font-semibold">{valor}</div>
      </CardContent>
    </Card>
  );
}

function LinhaOperacao({ op }: { op: OperacaoAlvo }) {
  const taxaSucesso = op.percentualSucesso === null
    ? '—'
    : op.percentualSucesso >= 99.95
      ? '100%'
      : `${op.percentualSucesso.toFixed(1)}%`;
  const corSucesso = corPctSucesso(op.percentualSucesso);
  const totalDesc = `${op.sucesso}/${op.sucesso + op.falha}`;
  const p95Formatado = op.p95LatenciaMs != null ? formatLatencia(op.p95LatenciaMs) : '—';
  return (
    <TableRow>
      <TableCell>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="break-all font-mono text-xs">{op.operation}</span>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground sm:hidden">
            <span className={`font-mono ${corSucesso}`}>{taxaSucesso}</span>
            <span>· p95 {p95Formatado}</span>
            {op.ultimoErroCodigo && (
              <span className="break-all font-mono">· {op.ultimoErroCodigo}</span>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden text-right sm:table-cell">
        <span className={`font-mono text-xs ${corSucesso}`}>{taxaSucesso}</span>
        <span className="ml-2 text-[11px] text-muted-foreground">{totalDesc}</span>
      </TableCell>
      <TableCell className="hidden text-right font-mono text-xs sm:table-cell">
        {p95Formatado}
      </TableCell>
      <TableCell className="hidden font-mono text-xs text-muted-foreground md:table-cell">
        {op.ultimoErroCodigo ?? '—'}
      </TableCell>
    </TableRow>
  );
}

function ListaIncidentes({ incidentes }: { incidentes: Incidente[] }) {
  return (
    <div className="space-y-2">
      {incidentes.map((inc) => (
        <Link
          key={inc.id}
          to={`/app/admin/monitoramento#incidente-${inc.id}`}
          className="flex items-center justify-between gap-3 rounded border border-border/60 px-3 py-2 text-xs transition-colors hover:bg-muted/40"
        >
          <div className="flex items-center gap-2">
            <span className="font-mono">#{inc.id}</span>
            <span className={inc.fechado_em ? 'text-muted-foreground' : 'font-medium'}>
              {inc.severidade}
            </span>
            <span className="text-muted-foreground">
              {inc.fechado_em ? 'fechado' : 'aberto'}
            </span>
          </div>
          <span className="text-muted-foreground">
            {fullDate(inc.aberto_em)}
          </span>
          <ExternalLink className="size-3 text-muted-foreground" />
        </Link>
      ))}
    </div>
  );
}

function construirLinkJobsDoAlvo(alvo: string): string {
  // Deep-link para a tela própria de Jobs com filtro pré-aplicado.
  // JobsPage lê ?alvo= via useSearchParams.
  const params = new URLSearchParams({ alvo });
  return `/app/admin/jobs?${params.toString()}`;
}

function construirLinkLogsDoAlvo(alvo: string): string {
  // Deep-link externo (abre em nova aba): tela Observabilidade aceita
  // alvo+period como filtros estruturados (contrato com a outra sessão).
  const params = new URLSearchParams({
    tab: 'logs',
    alvo,
    period: '24h',
    level: 'ALL',
  });
  return `/app/admin/observabilidade?${params.toString()}`;
}

function formatPercentual(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return '—';
  if (valor >= 99.95) return '100%';
  if (valor < 10) return `${valor.toFixed(2)}%`;
  return `${valor.toFixed(1)}%`;
}

function formatLatencia(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function corPctSucesso(pct: number | null): string {
  if (pct === null) return 'text-muted-foreground';
  if (pct >= 99) return 'text-green-700 dark:text-green-400';
  if (pct >= 90) return 'text-yellow-700 dark:text-yellow-400';
  return 'text-red-700 dark:text-red-400';
}
